import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
/** components **/
import { SurveySimpleLayout } from 'components/organisms';
import { RentPlanModal } from 'components/molecules/RentPlanModal';
/** lib **/
import { propertyData2, RentPlan } from 'lib/rent-plans';
/** styles **/
import s from '../styles/pages/survey-rent02.module.scss';
/** images **/
import SurveyArea04 from 'assets/images/surveyarea04.png';
import SurveyArea05 from 'assets/images/surveyarea05.png';
import SurveyArea06 from 'assets/images/surveyarea06.png';
import PeBird from 'assets/images/pe.png';
import { ReactComponent as TopHuman } from 'assets/images/top-human.svg';
import { ReactComponent as GreenCircleArrow } from 'assets/images/green-circle-arrow.svg';
import { ReactComponent as BlackCircleArrow } from 'assets/images/black-circle-arrow.svg';
// Swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const SelectCheckIcon = () => (
  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect y="0.5" width="16" height="16" rx="8" fill="white"/>
    <path d="M11.2069 5.29244C11.5975 4.902 12.2305 4.90194 12.621 5.29244C13.0114 5.68294 13.0114 5.68294 12.621 6.7065L7.63077 11.6958C7.62733 11.6993 7.6245 11.704 7.621 11.7075C7.40112 11.9272 7.10431 12.0233 6.81729 11.9956C6.74267 11.9884 6.66936 11.9706 6.59756 11.9467C6.48552 11.9094 6.37779 11.8539 6.28213 11.7758C6.25593 11.7545 6.23038 11.7309 6.20596 11.7065L6.20499 11.7055L3.37881 8.87838C2.98832 8.48785 2.9883 7.85483 3.37881 7.46431C3.76934 7.07401 4.40242 7.07389 4.79288 7.46431L6.91299 9.58443L11.2069 5.29244Z" fill="#00B66A"/>
  </svg>
);

export const SurveyRent02: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedFloorPlan, setSelectedFloorPlan] = useState<string>('');
  const [selectedRentPlan, setSelectedRentPlan] = useState<RentPlan | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [currentModalPlan, setCurrentModalPlan] = useState<RentPlan | null>(null);
  const [viewingPlan, setViewingPlan] = useState<RentPlan | null>(null); // モーダルで現在表示中のプラン
  const mansion01Plan = location.state?.mansion01Plan || null;
  
  // Get selected floor plan from previous page or default to 1ldk
  useEffect(() => {
    const floorPlanFromState = location.state?.selectedFloorPlan || '1ldk';
    setSelectedFloorPlan(floorPlanFromState);
  }, [location]);
  
  // Get property data for selected floor plan
  const currentProperty = propertyData2.find(p => p.floorPlanId === selectedFloorPlan) || propertyData2[0];
  
  // Image mapping
  const imageMap: { [key: string]: string } = {
    'surveyarea04.png': SurveyArea04,
    'surveyarea05.png': SurveyArea05,
    'surveyarea06.png': SurveyArea06
  };
  
  const handleRentPlanClick = (plan: RentPlan) => {
    setCurrentModalPlan(plan);
    setViewingPlan(plan);
    setModalOpen(true);
  };
  
  const handleRentPlanSelect = (plan: RentPlan) => {
    setSelectedRentPlan(plan);
  };
  
  const handleProceed = () => {
    if (selectedRentPlan) {
      navigate('/survey/form', { 
        state: { 
          selectedFloorPlan,
          mansion01Plan,
          mansion02Plan: selectedRentPlan
        }
      });
    }
  };

  return (
    <SurveySimpleLayout>
      <div className={s.surveyRent02Wrapper}>
        {/* ステップインジケーター */}
        <section className={s.stepIndicator}>
          <div className={s.stepWrapper}>
            <div className={`${s.step} ${s.completed}`}>
              <span>01</span>
            </div>
            <div className={`${s.step} ${s.active}`}>
              <span>02</span>
            </div>
            <div className={s.step}>
              <span>03</span>
            </div>
          </div>
        </section>

        {/* ページタイトル */}
        <section className={s.titleSection}>
          <p className={s.description}>
            3種類のプランがある場合、<br />
            あなたはどれを選びますか？
          </p>
          <p className={s.supplementDescription}>
            ※タップするとプランの詳細をご覧いただけます。
          </p>

          {/* プラン選択 */}
          <div className={s.rentPlanOptions}>
            {currentProperty.rentPlans.map((plan) => (
              <div key={plan.id} className={s.rentPlanWrapper}>
                {selectedRentPlan?.id === plan.id && (
                  <img src={PeBird} alt="" className={s.selectedBird} />
                )}
                <button
                  className={`${s.rentPlanButton} ${selectedRentPlan?.id === plan.id ? s.selected : ''}`}
                  onClick={() => handleRentPlanClick(plan)}
                >
                  <div className={`${s.planCircle} ${selectedRentPlan?.id === plan.id ? s.selected : ''}`}>
                    <span className={s.planLabel}>{plan.label}</span>
                  </div>
                  <div className={s.planInfo}>
                    <span className={s.planRentLabel}>賃料</span>
                    <div className={s.priceWrapper}>
                      <span className={s.planPrice}>{plan.price.replace('万', '')}</span>
                      <span className={s.priceUnit}>万円/月</span>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* 物件サンプルセクション */}
        <section className={s.propertySampleSection}>
          <div className={s.propertyHeader}>
            <div>
              <h3 className={s.propertyTitle} data-area={currentProperty.area.split('\n')[0]}>
              </h3>
              <div className={s.stationName}>
                <span className={s.station}>{currentProperty.station}</span>
                <span className={s.line}>駅／{currentProperty.area.split('\n')[1].split('／')[1]}</span>
              </div>
              <p className={s.walkTime}>{currentProperty.walkTime}</p>
            </div>
            <div className={s.tags}>
              <span className={s.tag}>{currentProperty.units}</span>
            </div>
          </div>

          <div className={s.sliderWrapper}>
            <Swiper
              modules={[Navigation, Pagination]}
              navigation={{
                nextEl: '.swiper-button-next-custom',
                prevEl: '.swiper-button-prev-custom',
              }}
              pagination={{
                el: '.swiper-pagination',
                clickable: true,
              }}
              spaceBetween={0}
              slidesPerView={1}
              className={s.propertySlider}
            >
              {currentProperty.images.map((image, index) => (
                <SwiperSlide key={index}>
                  <div className={s.slideImage}>
                    <img src={imageMap[image]} alt={`物件画像 ${index + 1}`} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <div className={s.sliderControls}>
            <button className={`swiper-button-prev-custom ${s.prevButton}`}>
              <BlackCircleArrow className={s.prevArrow} />
            </button>
            <div className={s.paginationWrapper}>
              <div className="swiper-pagination" />
            </div>
            <button className={`swiper-button-next-custom ${s.nextButton}`}>
              <BlackCircleArrow className={s.nextArrow} />
            </button>
          </div>
        </section>

        {/* 補足セクション */}
        <section className={s.supplementSection}>
          <div className={s.supplementContent}>
            <p className={s.supplementText}>
              事業化検討中につき実際に物件が存在、または<br />
              物件開発が予定されている訳ではございません。<br />
              あくまでも"このエリアにあったらという妄想"<br />
              の物件としてご理解ください。<br />
              サービス内容や賃料等も実際の物件開発時には<br />
              変更となる可能性がございます。
            </p>
            <TopHuman className={s.conciergeImage} />
          </div>
          
          <div className={s.navigationButtons}>
            <Link to="/survey/rent01" className={s.backButton} state={{ selectedFloorPlan, selectedRentPlan: mansion01Plan }}>
              戻る
            </Link>
            <button 
              className={s.proceedButton} 
              onClick={handleProceed}
              disabled={!selectedRentPlan}
            >
              <span className={s.proceedButtonText}>進む</span>
              <GreenCircleArrow className={s.proceedButtonIcon} />
            </button>
          </div>
        </section>
        
        {/* モーダル */}
        <RentPlanModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          plans={currentProperty.rentPlans}
          initialPlan={currentModalPlan}
          onSelect={handleRentPlanSelect}
          onSlideChange={(plan) => setViewingPlan(plan)}
        />
        
        {/* 固定選択ボタン */}
        {modalOpen && viewingPlan && (
          <div className={s.fixedSelectButton}>
            <button className={s.selectButton} onClick={() => {
              setSelectedRentPlan(viewingPlan);
              setModalOpen(false);
            }}>
              <SelectCheckIcon />
              このプランを選択する
            </button>
          </div>
        )}
      </div>
    </SurveySimpleLayout>
  );
};